import {
  Alert,
  AlertDescription,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  FormHelperText,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { useAuth } from 'src/utils/auth';
import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { gaEvent } from 'src/utils/gtag';
import { MarketplaceContext } from 'src/contexts/marketplace';
import SignupFormSkeleton from 'src/components/skeletons/signup-form-skeleton';
import SocialLoginRow from 'src/components/authentication/SocialLoginRow';
import { useOnboardingTheme } from 'src/contexts/onboardingTheme';
import { useMarketingOnboardingSafe } from 'src/contexts/marketingOnboarding';
import * as Sentry from '@sentry/react';
import { SignupResponse } from 'src/api/v1-api';

interface SignupFormProps {
  onClose?: () => void;
  setShowSignUp?: (show: boolean) => void;
  sendToAccountInfo?: boolean;
  recaptchaSiteKey?: string;
  returnUrl?: string;
}

const SignupForm: React.FC<SignupFormProps> = ({
  onClose,
  setShowSignUp,
  sendToAccountInfo = true,
  recaptchaSiteKey,
  returnUrl,
}) => {
  const { signup, isAuthenticated } = useAuth();
  const router = useRouter();
  const toast = useToast();
  const { marketplace, marketplaceSlug, isLoading } = useContext(MarketplaceContext);
  const { isOnboarding } = useOnboardingTheme();

  // Get marketing_slug from onboarding context if we're in that flow
  const marketingOnboardingContext = useMarketingOnboardingSafe();
  const marketingSlug = marketingOnboardingContext?.state?.marketing_slug;
  const isMarketingOnboarding = !!marketingOnboardingContext;

  const isInitializing =
    isMarketingOnboarding && (!router.isReady || marketingOnboardingContext?.isLoading);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { isOpen: showPassword, onToggle: togglePasswordVisibility } = useDisclosure();

  const handleSuccessfulSignup = async () => {
    if (onClose) {
      onClose();
    }

    // Check for returnUrl query parameter first (for post-signup redirects)
    const queryReturnUrl = router.query.returnUrl as string | undefined;
    if (queryReturnUrl && typeof queryReturnUrl === 'string') {
      if (queryReturnUrl.startsWith('/') && !queryReturnUrl.startsWith('//')) {
        await router.push(queryReturnUrl);
        return;
      }
    }

    // Check for a passed returnUrl prop
    if (returnUrl && typeof returnUrl === 'string') {
      if (returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
        await router.push(returnUrl);
        return;
      }
    }

    // Check for marketing onboarding redirect
    const redirectUrl = marketingOnboardingContext?.state?.redirectAfterLogin;
    if (redirectUrl) {
      marketingOnboardingContext?.updateState({ redirectAfterLogin: undefined });
      await router.push(redirectUrl);
      return;
    }

    // Default redirect to home
    if (!onClose || router.asPath.endsWith('/signup')) {
      await router.push('/');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      handleSuccessfulSignup();
    }
  }, [isAuthenticated, router]);

  const handleSignup = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    let recaptchaToken: string | null = null;
    if (recaptchaSiteKey) {
      if (typeof window !== 'undefined' && (window as any).grecaptcha) {
        try {
          recaptchaToken = await new Promise<string | null>((resolve, reject) => {
            (window as any).grecaptcha.enterprise.ready(async () => {
              try {
                const token = await (window as any).grecaptcha.enterprise.execute(
                  recaptchaSiteKey,
                  {
                    action: 'signup',
                  },
                );

                resolve(token);
              } catch (error) {
                Sentry.captureException(error, {
                  tags: { component: 'SignupForm', errorType: 'recaptcha_error_execute' },
                });
                resolve(null);
              }
            });
          });
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'SignupForm', errorType: 'recaptcha_error_ready' },
            extra: {
              siteKey: recaptchaSiteKey,
              grecaptchaAvailable: !!(window as any).grecaptcha,
            },
          });
          recaptchaToken = null;
        }
      }
    }

    if (isMarketingOnboarding && !marketingSlug) {
      toast({
        title: 'Error',
        description: 'Missing marketing information. Please start over from the original link.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    const names = name.split(' ');
    const firstName = names.shift();
    const lastName = names.join(' ');

    if (!marketplaceSlug) return;

    try {
      const response = await signup(
        email,
        password,
        firstName,
        lastName,
        marketplaceSlug,
        isMarketingOnboarding && marketplaceSlug === 'admin' ? marketingSlug : undefined,
        recaptchaToken || undefined,
      );

      // Check for DRF validation errors
      if (
        response.email?.[0] === 'A user with that email already exists.' ||
        response.username?.[0] === 'A user with that username already exists.'
      ) {
        setLoading(false);

        toast({
          title: 'Account Exists',
          description: 'An account with this email already exists. Please log in.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });

        if (setShowSignUp) {
          setShowSignUp(false);
        } else {
          router.push('/login');
        }

        return;
      }

      // Handle spam email case
      if (response.email?.[0] === 'This email is not authorized.') {
        toast({
          title: 'Invalid Email',
          description: 'This email address cannot be used for registration.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      const passwordErrors = response.password || (response as any).error?.password;
      if (passwordErrors && Array.isArray(passwordErrors)) {
        const errorMessage = passwordErrors.join(' ');
        toast({
          title: 'Password Error',
          description: errorMessage,
          status: 'error',
          duration: 7000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      if (response.access === undefined) {
        if (response?.detail === 'The account is inactive') {
          setSuccessMessage('Verification Email has been sent to your email address.');
          setEmail('');
          setPassword('');
          setName('');
        } else {
          toast({
            title: 'Error',
            description: 'Something went wrong. Please try again.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
        setLoading(false);
      } else {
        gaEvent({ action: 'sign_up', category: 'engagement', label: 'method', value: `${email}` });
        if (
          response.has_user_roles !== undefined &&
          response.has_user_roles > 0 &&
          sendToAccountInfo
        ) {
          router.push('/account/info');
        } else {
          handleSuccessfulSignup();
        }
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (
    !isOnboarding &&
    (isLoading || (!marketplace && marketplaceSlug !== 'admin') || !marketplaceSlug)
  ) {
    return <SignupFormSkeleton />;
  }

  if (isInitializing) {
    return <SignupFormSkeleton />;
  }

  if (isMarketingOnboarding && !marketingSlug) {
    return (
      <Stack spacing="6" p={8}>
        <Alert status="error">
          <AlertDescription>
            Missing marketing information. Please use the original signup link you received.
          </AlertDescription>
        </Alert>
        <Button colorScheme="primary" onClick={() => router.push('/')}>
          Go to Home
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing="0">
      <Stack spacing="0" align="center">
        <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
          <Heading size={{ base: 'sm', md: 'md' }}>Create an account</Heading>
        </Stack>
      </Stack>
      {!isOnboarding && (
        <HStack justify="center" pt={{ base: '2', sm: '4' }}>
          <Text color="muted">Already have an account?</Text>
          {setShowSignUp ? (
            <Button
              variant="link"
              colorScheme="primary"
              onClick={() => setShowSignUp && setShowSignUp(false)}
              data-test="login-link"
            >
              Log in
            </Button>
          ) : (
            <NextLink href="/login">
              <Button variant="link" colorScheme="primary" size="sm">
                Log in
              </Button>
            </NextLink>
          )}
        </HStack>
      )}
      <Box py={{ base: '0', sm: '4' }} px={{ base: '4', sm: '10' }} bg="formBG">
        <SocialLoginRow isRegister={true} onClose={onClose} returnUrl={returnUrl} />
        <form onSubmit={handleSignup}>
          <Stack spacing="6">
            <Stack spacing="5">
              <FormControl isRequired>
                <FormLabel htmlFor="name">Name</FormLabel>
                <Input
                  id="name"
                  type="text"
                  onChange={(e) => setName(e.target.value)}
                  data-test="name-input"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="email">Email</FormLabel>
                <Input
                  id="email"
                  type="email"
                  onChange={(e) => setEmail(e.target.value)}
                  data-test="email-input"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="password">Password</FormLabel>
                <InputGroup>
                  <InputRightElement>
                    <IconButton
                      variant="link"
                      aria-label={showPassword ? 'Mask password' : 'Reveal password'}
                      icon={showPassword ? <HiEyeOff /> : <HiEye />}
                      onClick={togglePasswordVisibility}
                    />
                  </InputRightElement>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    onChange={(e) => setPassword(e.target.value)}
                    data-test="password-input"
                    minLength={8}
                  />
                </InputGroup>
                <FormHelperText color="muted">At least 8 characters long</FormHelperText>
              </FormControl>
            </Stack>
            <Stack spacing="6">
              <Button
                disabled={!marketplaceSlug}
                colorScheme="primary"
                size="md"
                type="submit"
                isLoading={loading}
                data-test="submit-btn"
              >
                Create account
              </Button>
            </Stack>
            {errorMessage && (
              <Alert status="error">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            {successMessage && (
              <Alert status="success">
                <AlertDescription>{successMessage}</AlertDescription>
              </Alert>
            )}
          </Stack>
        </form>
      </Box>
    </Stack>
  );
};

export default SignupForm;
